import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Users, UserCheck, Route, UserRoundCheck, Clock, Download, Target, TrendingUp, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import Pagination from './Pagination';
import { format } from 'date-fns';
import { useReportStats, useUserSessions } from '@/hooks/queries/useReports';
import { useQueryClient } from '@tanstack/react-query';
import useAuth from '@/hooks/stores/useAuth';
import { reportService } from '@/services/ReportService';

const formatTimeString = (timeString) => {
  if (!timeString) return '0s';
  
  const [hours, minutes, seconds] = timeString.split(':').map(num => parseInt(num));
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
};


const ReportCard = ({ title, value, isLoading, icon: Icon }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-stroke dark:border-dark-3 p-6 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
      <div className="flex items-center justify-between mb-4">
        <Icon className="w-8 h-8 text-[#F16745]" />
        <div className="bg-[#F16745]/10 rounded-full p-2">
          <div className="w-2 h-2 rounded-full bg-[#F16745]"></div>
        </div>
      </div>
      <h3 className="text-sm font-medium text-body-color dark:text-dark-6 mb-2">{title}</h3>
      {isLoading ? (
        <div className="h-[36px] flex items-center">
          <div className="flex items-center space-x-1">
            <div className="h-2 w-2 bg-[#F16745] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="h-2 w-2 bg-[#F16745] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="h-2 w-2 bg-[#F16745] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      ) : (
        <p className="text-2xl font-bold text-gray-700 dark:text-dark-6 tracking-tight leading-tight min-h-[36px]">{value}</p>
      )}
    </div>
  );
};

const Reports = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const searchInputRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [exclude, setExclude] = useState(false);
  
  // Add console logs to debug
  console.log('Auth state:', auth);
  console.log('User role:', auth?.userRole);
  
  // Use React Query for dashboard stats (manual control)
  const { 
    data: statsData, 
    isLoading: isStatsLoading, 
    isError: isStatsError,
    refetch: refetchStats,
    isFetching: isStatsFetching
  } = useReportStats(exclude, true); // enable auto-fetch
  
  // Use React Query for user sessions with search params (auto-fetch)
  const { 
    data: sessionsResponse, 
    isLoading: isSessionsLoading,
    isError: isSessionsError,
    isFetching: isSessionsFetching
  } = useUserSessions(currentPage, searchParams, exclude);
  
  // Prepare stats object from React Query data
  const stats = {
    totalAccounts: statsData?.counts?.total || 0,
    activeAccounts: statsData?.activation_counts?.activated || 0,
    completedJourneys: statsData?.form_completion?.greater_than_75_percent || 0,
    completion51to75: statsData?.form_completion?.greater_than_50_percent || 0,
    completion26to50: statsData?.form_completion?.greater_than_25_percent || 0,
    completion0to25: statsData?.form_completion?.less_than_or_equal_25_percent || 0,
    totalLogins: statsData?.totallogins || 0,
    avgSessionLength: formatTimeString(statsData?.average_session_length_formatted) || '0s'
  };
  
  // Get role stats if available
  const roleStats = statsData?.counts ? 
    (() => {
      const { total, ...roleData } = statsData.counts;
      return roleData;
    })() : 
    {};
  
  // Extract user sessions and pagination from React Query
  const userSessions = sessionsResponse?.user_sessions || [];
  const paginationData = sessionsResponse?.pagination || {
    current_page: 1,
    total: 0,
    per_page: 10,
    last_page: 1
  };

  // Check if user has Report role
  const isReportRole = auth?.userRole?.toLowerCase() === 'reporting';
  console.log('Is Report role?', isReportRole);

  // Handle input changes with debouncing for empty input
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // We'll debounce the empty string check to avoid immediate API calls on backspace
    if (value === '') {
      // Set a small timeout to debounce clearing
      setTimeout(() => {
        setDebouncedSearchTerm('');
      }, 300);
    } else {
      setDebouncedSearchTerm(value);
    }
  };
  
  // Effect to auto-clear search when input becomes empty
  useEffect(() => {
    if (debouncedSearchTerm === '' && Object.keys(searchParams).length > 0) {
      clearSearch();
    }
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll the table container to top smoothly
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Add a function to handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Create a new searchParams object based on the searchTerm
    const newParams = {};
    
    // Check if searchTerm contains @ which likely means it's an email
    if (searchTerm.includes('@')) {
      newParams.email = searchTerm;
    } else if (searchTerm.trim()) {
      newParams.name = searchTerm;
    }
    
    // Update search params state
    setSearchParams(newParams);
    
    // Reset to first page when searching
    setCurrentPage(1);
    
    // Maintain focus on the search input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Add a function to clear the search
  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSearchParams({});
    setCurrentPage(1);
    
    // Maintain focus on the search input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };
  
  // Create loading indicator component to reuse
  const LoadingIndicator = () => (
    <div className="flex justify-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#F16745]"></div>
    </div>
  );

  const refreshData = () => {
    refetchStats();
  };

  // Add download handler
  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await reportService.downloadReports(searchParams, exclude);
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Failed to download report');
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handler for checkbox
  const handleExcludeChange = (e) => {
    setExclude(e.target.checked);
    setTimeout(() => {
      refetchStats();
    }, 0);
  };

  return (
    <div className="px-7 sm:px-9 lg:px-10 py-12 w-full mx-auto">
      {/* Header */}
      <div className="mb-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-[#F16745] dark:text-[#F16745] mb-2">PostScrypt Reports</h1>
            <p className="text-base text-body-color dark:text-dark-6">Track your platform&apos;s performance and user engagement</p>
            <div className="mt-2">
              <label className="flex items-center gap-1 ml-0 cursor-pointer select-none transition-colors duration-200 hover:text-[#F16745]">
                <input
                  type="checkbox"
                  checked={exclude}
                  onChange={handleExcludeChange}
                  className="form-checkbox h-4 w-4 text-[#F16745] border-gray-300 rounded focus:ring-[#F16745] transition-colors duration-200 hover:border-[#F16745]"
                />
                <span className="text-sm text-gray-600 transition-colors duration-200">Hide Default Users</span>
              </label>
            </div>
          </div>
          <button
            onClick={refreshData}
            disabled={isStatsFetching}
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#F16745] to-[#F16745]/80 p-0.5 text-sm font-medium text-white hover:text-white focus:outline-none focus:ring-4 focus:ring-[#F16745]/50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <span className="relative flex items-center gap-2 rounded-md bg-[#F16745] px-5 py-2.5 transition-all duration-75 ease-in group-hover:bg-opacity-0">
              <RefreshCw 
                className={`w-5 h-5 ${
                  isStatsFetching 
                    ? 'animate-[spin_1s_linear_infinite]' 
                    : 'transition-transform duration-300 group-hover:rotate-180'
                }`}
              />
              <span className="whitespace-nowrap text-base">
                {isStatsFetching ? 'Refreshing...' : 'Refresh'}
              </span>
            </span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <ReportCard
          title="Total PS Accounts"
          value={stats.totalAccounts.toLocaleString()}
          isLoading={isStatsLoading}
          icon={Users}
        />
        <ReportCard
          title="Activated PS Accounts"
          value={stats.activeAccounts.toLocaleString()}
          isLoading={isStatsLoading}
          icon={UserCheck}
        />
        <ReportCard
          title="Completed Journeys"
          value={stats.completedJourneys.toLocaleString()}
          isLoading={isStatsLoading}
          icon={Route}
        />
        <ReportCard
          title="≤75% Complete"
          value={stats.completion51to75.toLocaleString()}
          isLoading={isStatsLoading}
          icon={Route}
        />
        <ReportCard
          title="≤50% Complete"
          value={stats.completion26to50.toLocaleString()}
          isLoading={isStatsLoading}
          icon={Route}
        />
        <ReportCard
          title="≤25% Complete"
          value={stats.completion0to25.toLocaleString()}
          isLoading={isStatsLoading}
          icon={Route}
        />
        <ReportCard
          title="Total Logins"
          value={stats.totalLogins.toLocaleString()}
          isLoading={isStatsLoading}
          icon={UserRoundCheck}
        />
        <ReportCard
          title="Average Session Length"
          value={stats.avgSessionLength}
          isLoading={isStatsLoading}
          icon={Clock}
        />
      </div>

      {/* Per User Statistics Section */}
      <div className="mt-10">
        <div className="flex items-center gap-4 mb-7">
          <h2 className="text-2xl font-bold text-[#F16745] dark:text-[#F16745]">Per User Statistics</h2>
        </div>
        <div ref={tableContainerRef} className="bg-white rounded-lg dark:bg-dark-2 shadow-card">
          {/* Table Top Section - Updated with form and search functionality */}
          <div className="flex items-center justify-between flex-wrap sm:flex-nowrap gap-4 border-b border-stroke dark:border-dark-3 pt-[8px] pb-[18px]">
            <div className="w-full sm:w-auto sm:flex-1 max-w-full sm:max-w-[420px]">
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    style={{ boxShadow: 'none' }}
                    className="h-12 w-full pl-10 pr-4 rounded-md border border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 hover:border-gray-400 focus:border-[#F16745] focus:outline-none focus:ring-0 transition-colors"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-[46px] top-0 h-full px-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <button 
                    type="submit"
                    className="absolute top-0 right-0 flex h-full w-[46px] items-center justify-center bg-[#F16745] text-white rounded-r-md"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17.2687 15.6657L12.6281 11.897C14.5406 9.28135 14.3437 5.54072 11.9531 3.17822C10.6875 1.9126 8.99995 1.20947 7.19995 1.20947C5.39995 1.20947 3.71245 1.9126 2.44683 3.17822C-0.168799 5.79385 -0.168799 10.0688 2.44683 12.6845C3.71245 13.9501 5.39995 14.6532 7.19995 14.6532C8.91558 14.6532 10.5187 14.0063 11.7843 12.8532L16.4812 16.6501C16.5937 16.7345 16.7343 16.7907 16.875 16.7907C17.0718 16.7907 17.2406 16.7063 17.3531 16.5657C17.5781 16.2845 17.55 15.8907 17.2687 15.6657ZM7.19995 13.3876C5.73745 13.3876 4.38745 12.8251 3.34683 11.7845C1.20933 9.64697 1.20933 6.1876 3.34683 4.07822C4.38745 3.0376 5.73745 2.4751 7.19995 2.4751C8.66245 2.4751 10.0125 3.0376 11.0531 4.07822C13.1906 6.21572 13.1906 9.6751 11.0531 11.7845C10.0406 12.8251 8.66245 13.3876 7.19995 13.3876Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </form>
            </div>
            { (isReportRole || auth?.userRole?.toLowerCase() === 'admin') && (
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#F16745] to-[#F16745]/80 p-0.5 text-sm font-medium text-white hover:text-white focus:outline-none focus:ring-4 focus:ring-[#F16745]/50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <span className="relative flex items-center gap-2 rounded-md bg-[#F16745] px-5 py-2.5 transition-all duration-75 ease-in group-hover:bg-opacity-0">
                  <Download className="w-5 h-5" />
                  <span className="whitespace-nowrap text-base">
                    {isDownloading ? 'Downloading...' : 'Download CSV'}
                  </span>
                </span>
              </button>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-stroke dark:border-dark-3">
                    <th className="px-6 pt-9 pb-6 text-left font-medium text-base text-body-color dark:text-dark-6">
                      Name
                    </th>
                    <th className="px-6 pt-9 pb-6 text-left font-medium text-base text-body-color dark:text-dark-6">
                      Email
                    </th>
                    <th className="px-6 pt-9 pb-6 text-left font-medium text-base text-body-color dark:text-dark-6">
                      Legal Advisor
                    </th>
                    <th className="px-6 pt-9 pb-6 text-left font-medium text-base text-body-color dark:text-dark-6">
                      Total Logins
                    </th>
                    <th className="px-6 pt-9 pb-6 text-left font-medium text-base text-body-color dark:text-dark-6">
                      Average Session Length
                    </th>
                    <th className="px-6 pt-9 pb-6 text-left font-medium text-base text-body-color dark:text-dark-6">
                      Created Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isSessionsLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center">
                        <LoadingIndicator />
                      </td>
                    </tr>
                  ) : userSessions.length > 0 ? (
                    userSessions.map((session) => (
                      <tr key={session.user_id} className="hover:bg-[#F16745]/5">
                        <td className="px-6 py-5 whitespace-nowrap">
                          <p className="text-base font-medium text-[#F16745]">{session.user_name}</p>
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                          {session.user_email}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                          {session.legal_advisor_name || 'N/A'}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                          {session.total_logins}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                          {formatTimeString(session.average_session_length_formatted)}
                        </td>
                        <td className="px-6 py-5 whitespace-nowrap text-sm text-body-color dark:text-dark-6">
                          {session.created_at ? format(new Date(session.created_at), 'MMM dd, yyyy') : 'N/A'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center text-body-color dark:text-dark-6">
                        No user sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile/Tablet View */}
          <div className="lg:hidden space-y-4 p-4">
            {isSessionsLoading ? (
              <LoadingIndicator />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {userSessions.length > 0 ? (
                  userSessions.map((session) => (
                    <div key={session.user_id} className="bg-white p-4 rounded-lg shadow">
                      <div className="space-y-3">
                        <div>
                          <div>
                            <h3 className="text-[#F16745] font-medium">{session.user_name}</h3>
                            <p className="text-sm mt-3 text-gray-600">{session.user_email}</p>
                          </div>
                        </div>
                        <div className="space-y-3 pt-2">
                          <div>
                            <p className="text-sm font-medium text-body-color dark:text-dark-6">Legal Advisor</p>
                            <p className="text-sm">{session.legal_advisor_name || 'N/A'}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <div>
                            <p className="text-sm font-medium text-body-color dark:text-dark-6">Total Logins</p>
                            <p className="text-lg">{session.total_logins}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-body-color dark:text-dark-6">Avg Session</p>
                            <p className="text-lg">{formatTimeString(session.average_session_length_formatted)}</p>
                          </div>
                        </div>
                        <div className="pt-2">
                          <p className="text-sm font-medium text-body-color dark:text-dark-6">Account Created Date</p>
                          <p className="text-sm">{session.created_at ? format(new Date(session.created_at), 'MMM dd, yyyy') : 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 text-center text-gray-500 py-4">
                    No user sessions found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Pagination - don't hide during fetching, only during initial loading */}
          {userSessions.length > 0 && !isSessionsLoading && (
            <div className="w-full">
              <Pagination
                currentPage={paginationData.current_page}
                totalPages={paginationData.last_page}
                onPageChange={handlePageChange}
                itemsPerPage={paginationData.per_page}
                totalItems={paginationData.total}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;